/**
 * Typescript wrapper for simple object copy through JSON
 */
export function jsonCopyObject<T extends object>(object: T): T {
  return JSON.parse(JSON.stringify(object));
}

// It works much better than loadash.isEqual
export function isJSONEqual<T extends object>(object1: T, object2: T): boolean {
  return JSON.stringify(object1) === JSON.stringify(object2);
}

/**
 * Simple object check.
 * @param item The value to check if it is a plain object.
 * @returns {boolean}
 */
export function isObject(item: unknown): item is Record<string, unknown> {
  return (item !== null && typeof item === 'object' && !Array.isArray(item));
}

/**
 * This function solves the TS issue where Object.entries() looses the type of the key if enums are used as keys.
 * @param obj
 */
export function objectEntries<K extends string | symbol, V>(obj: Record<K, V>): [K, V][] {
  return Object.entries(obj) as [K, V][];
}

/**
 * Typed wrapper for Object.entries() that preserves numeric keys.
 * Object.entries() always returns keys as strings; this function converts them back to numbers.
 * @param obj An object whose keys are numbers.
 */
export function objectNumericEntries<K extends number, V>(obj: Record<K, V>): [K, V][] {
  return Object.entries(obj).map(([
    key,
    value,
  ]) => [
    Number(key),
    value,
  ]) as [K, V][];
}

/**
 * This function solves the TS issue where Object.keys() looses the type of the key if enums are used as keys.
 * @param obj
 */
export function objectKeys<K extends string | symbol>(obj: Record<K, any>): K[] {
  return Object.keys(obj) as K[];
}

/**
 * Typed wrapper for Object.keys() that preserves numeric keys.
 * Object.keys() always returns keys as strings; this function converts them back to numbers.
 * @param obj An object whose keys are numbers.
 */
export function objectNumericKeys<K extends number>(obj: Record<K, any>): K[] {
  return Object.keys(obj).map((key) => Number(key)) as K[];
}
